import { LegacyOrganization } from '../types/legacy';
import { getStoredLegacyToken } from './legacyAuthService';

const ORG_LIMITS = {
  name: 50,
  logo: 50,
  website: 128,
  email: 255,
} as const;

function buildAuthHeaders(): HeadersInit {
  const token = getStoredLegacyToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
  }
  return headers;
}

function normalizeString(value: unknown, maxLen: number): string {
  return String(value ?? '').trim().slice(0, maxLen);
}

async function parseResponse<T>(response: Response): Promise<T> {
  const raw = await response.text();
  let parsed: unknown = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    const message =
      (parsed && typeof parsed === 'object' && 'message' in parsed && typeof (parsed as { message?: unknown }).message === 'string'
        ? String((parsed as { message: string }).message)
        : raw) || `API error (${response.status})`;
    throw new Error(message);
  }

  return (parsed as T) || ({} as T);
}

function normalizeOrganizationList(payload: unknown): LegacyOrganization[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as LegacyOrganization[];
  if (typeof payload === 'object') {
    const body = payload as { data?: unknown; content?: unknown; items?: unknown };
    if (Array.isArray(body.data)) return body.data as LegacyOrganization[];
    if (Array.isArray(body.content)) return body.content as LegacyOrganization[];
    if (Array.isArray(body.items)) return body.items as LegacyOrganization[];
  }
  return [];
}

export async function getCurrentLegacyUserProfile(): Promise<Record<string, unknown>> {
  const response = await fetch('/api/user', {
    method: 'GET',
    headers: buildAuthHeaders(),
  });
  return parseResponse<Record<string, unknown>>(response);
}

export async function getOrganizationById(id: number): Promise<LegacyOrganization> {
  const response = await fetch(`/api/organization/${id}`, {
    method: 'GET',
    headers: buildAuthHeaders(),
  });
  return parseResponse<LegacyOrganization>(response);
}

export async function listOrganizations(): Promise<LegacyOrganization[]> {
  const response = await fetch('/api/organization/list?page=0&size=500', {
    method: 'GET',
    headers: buildAuthHeaders(),
  });
  const payload = await parseResponse<unknown>(response);
  return normalizeOrganizationList(payload);
}

export async function updateOrganization(payload: LegacyOrganization): Promise<LegacyOrganization> {
  const normalizedPayload: LegacyOrganization = {
    ...payload,
    id: Number(payload.id),
    name: normalizeString(payload.name, ORG_LIMITS.name),
    logo: normalizeString(payload.logo, ORG_LIMITS.logo),
    website: normalizeString(payload.website, ORG_LIMITS.website),
    email: normalizeString(payload.email, ORG_LIMITS.email),
    guarded: Boolean(payload.guarded),
  };
  const response = await fetch('/api/organization', {
    method: 'PUT',
    headers: buildAuthHeaders(),
    body: JSON.stringify(normalizedPayload),
  });
  return parseResponse<LegacyOrganization>(response);
}

export async function createOrganization(payload: Pick<LegacyOrganization, 'name' | 'email' | 'website' | 'logo' | 'guarded'>): Promise<LegacyOrganization> {
  const normalizedPayload = {
    name: normalizeString(payload.name, ORG_LIMITS.name),
    email: normalizeString(payload.email || '', ORG_LIMITS.email),
    website: normalizeString(payload.website || '', ORG_LIMITS.website),
    logo: normalizeString(payload.logo || '', ORG_LIMITS.logo),
    guarded: Boolean(payload.guarded),
  };
  const response = await fetch('/api/organization', {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify(normalizedPayload),
  });
  return parseResponse<LegacyOrganization>(response);
}
